import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Users,
  Monitor,
  Smartphone,
  CheckCircle,
  Play,
  Pause,
  StopCircle,
  ArrowRight,
  Shield,
  RefreshCw,
  ArrowLeft,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { inPersonApi, envelopeApi } from "../../services/api";

export const InPersonSigningHost = () => {
  const { t } = useTranslation();
  const { sessionId, envelopeId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showPinModal, setShowPinModal] = useState(false);
  const [pin, setPin] = useState("");
  const [error, setError] = useState(null);

  // ============================================
  // TanStack Query - Fetch Session & Envelope Data
  // ============================================
  const {
    data: queryData,
    isLoading: loading,
    refetch: loadData,
  } = useQuery({
    queryKey: ["in-person-host", sessionId, envelopeId],
    queryFn: async () => {
      let sessionData = null;
      let envelopeData = null;

      // If we have a sessionId, load the session first
      if (sessionId) {
        const sessionRes = await inPersonApi.getById(sessionId);
        sessionData = sessionRes.data?.data;
        if (sessionData) {
          // Now load the envelope using the session's envelopeId
          const envelopeRes = await envelopeApi.getById(sessionData.envelopeId);
          envelopeData = envelopeRes.data?.data;
        } else {
          throw new Error("Session not found");
        }
      } else if (envelopeId) {
        // Original flow: load envelope and check for existing session
        const envelopeRes = await envelopeApi.getById(envelopeId);
        envelopeData = envelopeRes.data?.data;

        // Check for existing active session
        const sessionsRes = await inPersonApi.list({ status: "active" });
        const activeSession = sessionsRes.data?.data?.find(
          (s) => s.envelopeId === envelopeId,
        );
        if (activeSession) {
          sessionData = activeSession;
        }
      } else {
        throw new Error("No session or envelope ID provided");
      }

      return { session: sessionData, envelope: envelopeData };
    },
    enabled: !!(sessionId || envelopeId),
    staleTime: 0,
    retry: false,
    meta: {
      onError: (err) => {
        console.error("Failed to load data:", err);
        setError(
          err.message ||
            err.response?.data?.message ||
            "Failed to load session data",
        );
      },
    },
  });

  // Extract data from query
  const session = queryData?.session;
  const envelope = queryData?.envelope;

  // ============================================
  // TanStack Query - Mutations
  // ============================================

  // Start session mutation
  const startSessionMutation = useMutation({
    mutationFn: () => inPersonApi.start(envelope.id, { pin: pin || undefined }),
    onSuccess: () => {
      setShowPinModal(false);
      queryClient.invalidateQueries({ queryKey: ["in-person-host"] });
    },
    onError: (err) => {
      console.error("Failed to start session:", err);
      alert(err.response?.data?.message || "Failed to start session");
    },
  });

  // Pause session mutation
  const pauseSessionMutation = useMutation({
    mutationFn: () => inPersonApi.pause(session.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["in-person-host"] });
    },
    onError: (err) => {
      console.error("Failed to pause session:", err);
    },
  });

  // Resume session mutation
  const resumeSessionMutation = useMutation({
    mutationFn: () => inPersonApi.resume(session.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["in-person-host"] });
    },
    onError: (err) => {
      console.error("Failed to resume session:", err);
    },
  });

  // Cancel session mutation
  const cancelSessionMutation = useMutation({
    mutationFn: () => inPersonApi.cancel(session.id),
    onSuccess: () => {
      navigate("/in-person");
    },
    onError: (err) => {
      console.error("Failed to cancel session:", err);
    },
  });

  // Handoff mutation
  const handoffMutation = useMutation({
    mutationFn: (recipientId) =>
      inPersonApi.handoff(session.sessionToken, recipientId, pin),
    onSuccess: (_, recipientId) => {
      navigate(
        `/in-person/${session.sessionToken}/sign/${recipientId}?hostSession=${session.id}`,
      );
    },
    onError: (err) => {
      console.error("Failed to handoff:", err);
      toast.error(
        err.response?.data?.message || "Failed to hand off to recipient",
      );
    },
  });

  // Derived states
  const starting = startSessionMutation.isPending;

  const startNewSession = () => {
    if (!envelope) return;
    startSessionMutation.mutate();
  };

  const pauseSession = () => {
    pauseSessionMutation.mutate();
  };

  const resumeSession = () => {
    resumeSessionMutation.mutate();
  };

  const cancelSession = () => {
    if (!confirm("Are you sure you want to cancel this session?")) return;
    cancelSessionMutation.mutate();
  };

  const handoffToRecipient = (recipientId) => {
    if (!session || !session.sessionToken || !session.id) {
      toast.error("Session not ready. Please refresh the page.");
      return;
    }
    handoffMutation.mutate(recipientId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <h2 className="text-lg font-semibold text-red-800">
            {t("errors.loadingSession")}
          </h2>
          <p className="text-red-600 mt-2">{error}</p>
          <button
            onClick={() => navigate("/in-person")}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Back to Sessions
          </button>
        </div>
      </div>
    );
  }

  if (!envelope) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
          <h2 className="text-lg font-semibold text-yellow-800">
            {t("envelopes.notFound")}
          </h2>
          <p className="text-yellow-600 mt-2">
            {t("errors.couldNotLoadDocument")}
          </p>
          <button
            onClick={() => navigate("/in-person")}
            className="mt-4 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
          >
            Back to Sessions
          </button>
        </div>
      </div>
    );
  }

  const pendingRecipients =
    envelope?.recipients?.filter(
      (r) => r.status !== "signed" && r.status !== "declined",
    ) || [];

  const completedRecipients =
    envelope?.recipients?.filter((r) => r.status === "signed") || [];

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate("/in-person")}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Sessions
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {t("inPerson.title")}
        </h1>
        <p className="text-gray-600">{envelope?.title}</p>
      </div>

      {!session ? (
        // Start Session View
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Monitor className="w-8 h-8 text-primary" />
          </div>
          <h2 className="text-xl font-semibold mb-2">
            {t("signing.startInPersonSession")}
          </h2>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Have all signers present? Start a session to collect signatures on
            this device.
          </p>

          <div className="bg-gray-50 rounded-lg p-4 mb-6 max-w-md mx-auto">
            <h3 className="font-medium mb-2">
              Pending Signers ({pendingRecipients.length})
            </h3>
            <div className="space-y-2">
              {pendingRecipients.map((r) => (
                <div key={r.id} className="flex items-center gap-2 text-sm">
                  <div className="w-2 h-2 bg-amber-500 rounded-full" />
                  <span>{r.name || r.email}</span>
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={() => setShowPinModal(true)}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 inline-flex items-center gap-2"
          >
            <Play className="w-5 h-5" />
            Start Session
          </button>
        </div>
      ) : (
        // Active Session View
        <div className="space-y-6">
          {/* Session Status */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div
                  className={`w-3 h-3 rounded-full ${
                    session.status === "active"
                      ? "bg-green-500 animate-pulse"
                      : "bg-amber-500"
                  }`}
                />
                <span className="font-medium capitalize">
                  {session.status} Session
                </span>
              </div>
              <div className="flex items-center gap-2">
                {session.status === "active" ? (
                  <button
                    onClick={pauseSession}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                    title={t("signing.inPerson.pauseSession")}
                  >
                    <Pause className="w-5 h-5" />
                  </button>
                ) : (
                  <button
                    onClick={resumeSession}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                    title={t("signing.inPerson.resumeSession")}
                  >
                    <Play className="w-5 h-5" />
                  </button>
                )}
                <button
                  onClick={cancelSession}
                  className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                  title={t("signing.inPerson.cancelSession")}
                >
                  <StopCircle className="w-5 h-5" />
                </button>
              </div>
            </div>

            {session.currentRecipientId && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
                <Smartphone className="w-6 h-6 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-900">
                    {t("inPerson.deviceHandedOff")}
                  </p>
                  <p className="text-sm text-blue-700">
                    Waiting for{" "}
                    {pendingRecipients.find(
                      (r) => r.id === session.currentRecipientId,
                    )?.name || "recipient"}{" "}
                    to sign
                  </p>
                </div>
                <button
                  onClick={loadData}
                  className="ml-auto p-2 text-blue-600 hover:bg-blue-100 rounded"
                >
                  <RefreshCw className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>

          {/* Pending Recipients */}
          {pendingRecipients.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="font-semibold mb-4">
                {t("signing.pendingSigners")}
              </h3>
              <div className="space-y-3">
                {pendingRecipients.map((recipient) => (
                  <div
                    key={recipient.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                        <Users className="w-5 h-5 text-gray-500" />
                      </div>
                      <div>
                        <p className="font-medium">
                          {recipient.name || "Unnamed"}
                        </p>
                        <p className="text-sm text-gray-500">
                          {recipient.email}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handoffToRecipient(recipient.id)}
                      disabled={
                        session.status !== "active" ||
                        session.currentRecipientId
                      }
                      className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Hand Off Device
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Completed Recipients */}
          {completedRecipients.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="font-semibold mb-4">{t("common.completed")}</h3>
              <div className="space-y-3">
                {completedRecipients.map((recipient) => (
                  <div
                    key={recipient.id}
                    className="flex items-center gap-3 p-4 bg-green-50 rounded-lg"
                  >
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium">
                        {recipient.name || "Unnamed"}
                      </p>
                      <p className="text-sm text-green-600">
                        {t("common.signed")}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Session Complete */}
          {pendingRecipients.length === 0 && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
              <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-green-900">
                {t("signing.allCollected")}
              </h3>
              <p className="text-green-700 mb-4">
                {t("signing.documentFullySigned")}
              </p>
              <button
                onClick={() => navigate(`/envelopes/${envelope.id}`)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                View Document
              </button>
            </div>
          )}

          {/* Session Info */}
          <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-4 h-4" />
              <span>
                Session started at{" "}
                {new Date(session.startedAt).toLocaleTimeString()}
              </span>
            </div>
            {session.handoffs?.length > 0 && (
              <p>{session.handoffs.length} handoff(s) recorded</p>
            )}
          </div>
        </div>
      )}

      {/* PIN Modal */}
      {showPinModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-sm p-6">
            <h2 className="text-lg font-semibold mb-4">
              {t("inPerson.setSessionPin")}
            </h2>
            <p className="text-gray-600 text-sm mb-4">
              Add a PIN to secure device handoffs. Recipients will need to enter
              this PIN to access the signing page.
            </p>
            <input
              aria-label="PIN"
              type="text"
              value={pin}
              onChange={(e) =>
                setPin(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              placeholder="4-6 digit PIN"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4 text-center text-2xl tracking-widest"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowPinModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={startNewSession}
                disabled={starting}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
              >
                {starting ? "Starting..." : "Start Session"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InPersonSigningHost;
